import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { raceAssetUploadService } from "./raceAssetUploadService";
import { racerDataService } from "./racerDataService";
import { profilePhotoPreferences } from "./profilePhotoPreferences";

export type PhotoSetupStatus = "pending" | "skipped" | "dismissed" | "completed";
export type ProfilePhotoState = {
  uid: string;
  photoUrl: string | null;
  isRacing: boolean;
  status: PhotoSetupStatus | null;
  reminderAfter: number;
  googlePhotoUrl: string | null;
};
const statuses: readonly string[] = ["pending", "skipped", "dismissed", "completed"];
const DAY_MS = 24 * 60 * 60 * 1000;

export function parseProfilePhotoState(uid: string, data: Record<string, unknown>): ProfilePhotoState {
  const status = data.profilePhotoSetupStatus;
  return {
    uid,
    isRacing: data.isRacing === true,
    photoUrl: typeof data.profilePhotoURLString === "string" ? data.profilePhotoURLString.trim() || null : null,
    status: typeof status === "string" && statuses.includes(status) ? status as PhotoSetupStatus : null,
    reminderAfter: typeof data.profilePhotoReminderAfter === "number" ? data.profilePhotoReminderAfter : 0,
    googlePhotoUrl: null,
  };
}

function requireOwner(uid: string) {
  if (!uid || auth.currentUser?.uid !== uid) throw new Error("Account changed.");
  return auth.currentUser;
}

export const profilePhotoService = {
  subscribe(uid: string, onData: (state: ProfilePhotoState) => void, onError: () => void) {
    const user = requireOwner(uid);
    const pending = profilePhotoPreferences.read(uid);
    if (pending) {
      void updateDoc(doc(db, "users", uid), {
        profilePhotoSetupStatus: pending.status, profilePhotoReminderAfter: pending.reminderAfter,
      }).then(() => {
        if (profilePhotoPreferences.read(uid)?.reminderAfter === pending.reminderAfter) profilePhotoPreferences.clear(uid);
      }).catch(() => console.warn("Unable to sync profile photo reminder preference."));
    }
    return onSnapshot(doc(db, "users", uid), (snapshot) => {
      if (!snapshot.exists() || auth.currentUser?.uid !== uid) return;
      const state = parseProfilePhotoState(uid, snapshot.data());
      const deferred = profilePhotoPreferences.read(uid);
      if (deferred) { state.status = deferred.status; state.reminderAfter = deferred.reminderAfter; }
      state.googlePhotoUrl = user.providerData.find((provider) => provider.providerId === "google.com")?.photoURL || null;
      onData(state);
    }, onError);
  },
  async defer(uid: string, dismiss: boolean) {
    requireOwner(uid);
    const preference = { status: dismiss ? "dismissed" as const : "skipped" as const, reminderAfter: Date.now() + DAY_MS };
    profilePhotoPreferences.save(uid, preference);
    await updateDoc(doc(db, "users", uid), {
      profilePhotoSetupStatus: preference.status,
      profilePhotoReminderAfter: preference.reminderAfter,
    });
    if (profilePhotoPreferences.read(uid)?.reminderAfter === preference.reminderAfter) profilePhotoPreferences.clear(uid);
  },
  async save(uid: string, selection: File | "google") {
    const user = requireOwner(uid);
    const candidate = user.providerData.find((provider) => provider.providerId === "google.com")?.photoURL;
    const url = selection === "google" ? candidate : await raceAssetUploadService.uploadUserProfilePhoto({ userId: uid, imageFile: selection });
    requireOwner(uid);
    if (!url || !url.startsWith("https://")) throw new Error("Photo unavailable.");
    // The shared profile is authoritative. Do not publish a provider image until
    // its owner explicitly chooses it; Auth's avatar is only a suggestion.
    await updateDoc(doc(db, "users", uid), { profilePhotoURLString: url, profilePhotoSetupStatus: "completed" });
    profilePhotoPreferences.clear(uid);
    racerDataService.clear();
  },
};

export type ProfilePhotoService = typeof profilePhotoService;
